import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Paper, Dialog, DialogTitle, DialogContent, DialogActions, Alert, FormControl, InputLabel, Select, MenuItem, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { apiGet, apiRequest } from '../../../utils/fetch';
import { AdSpacePaymentDetailsInterface, GenericResponseInterface } from '../../../interfaces/interfaces';
import { getPaymentDetailsColumns } from './PaymentDetailsColumns';
import { PaymentDetailsUpdateDialog } from './PaymentDetailsUpdateDialog';

const PaymentDetailsAdminList: React.FC = () => {
    const [payments, setPayments] = useState<AdSpacePaymentDetailsInterface[]>([]);
    const [payment, setPayment] = useState<AdSpacePaymentDetailsInterface | null>(null);
    const [paymentId, setPaymentId] = useState('');
    const [userId, setUserId] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [error, setError] = useState<string | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [paymentToDelete, setPaymentToDelete] = useState<AdSpacePaymentDetailsInterface | null>(null);
    const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
    const [paymentToUpdate, setPaymentToUpdate] = useState<AdSpacePaymentDetailsInterface | null>(null);
    const [updateError, setUpdateError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Fetch all payments
    const handleGetAllPayments = async () => {
        setError(null);
        setPayment(null);
        setSuccess(null);
        try {
            const result = await apiGet<GenericResponseInterface<AdSpacePaymentDetailsInterface[]>>('/paymentDetails');
            if (result.status && result.data) {
                setPayments(result.data);
            } else {
                setError(result.message || 'Failed to fetch payment details');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch payment details');
        }
    };

    // Fetch payment by id
    const handleGetPaymentById = async () => {
        setError(null);
        setPayments([]);
        setSuccess(null);
        if (!paymentId) return setError('Please enter a Payment ID');
        try {
            const result = await apiGet<GenericResponseInterface<AdSpacePaymentDetailsInterface>>(`/paymentDetails/${paymentId}`);
            if (result.status && result.data) {
                setPayment(result.data);
            } else {
                setError(result.message || 'Failed to fetch payment details');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch payment details');
        }
    };

    // Fetch payments by user ID
    const handleGetPaymentsByUserId = async () => {
        setError(null);
        setPayment(null);
        setSuccess(null);
        if (!userId) return setError('Please enter a User ID');
        try {
            const result = await apiGet<GenericResponseInterface<AdSpacePaymentDetailsInterface[]>>(`/paymentDetails/user/${userId}`);
            if (result.status && result.data) {
                setPayments(result.data);
            } else {
                setError(result.message || 'Failed to fetch payment details');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch payment details');
        }
    };

    // Fetch payments by status
    const handleGetPaymentsByStatus = async (status: string) => {
        setError(null);
        setPayment(null);
        setSuccess(null);
        if (status === 'all') {
            handleGetAllPayments();
            return;
        }
        try {
            const result = await apiGet<GenericResponseInterface<AdSpacePaymentDetailsInterface[]>>(`/paymentDetails/status/${status}`);
            if (result.status && result.data) {
                setPayments(result.data);
            } else {
                setError(result.message || 'Failed to fetch payment details');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch payment details');
        }
    };

    // Delete payment (not used in UI - for safety)
    const handleDeletePayment = async () => {
        if (!paymentToDelete) return;
        setError(null);
        setSuccess(null);
        try {
            const result = await apiRequest<GenericResponseInterface>(`/paymentDetails/delete/${paymentToDelete.id}`, 'DELETE');
            if (result.status) {
                setSuccess(result.message || 'Payment details deleted successfully');
                setDeleteDialogOpen(false);
                setPaymentToDelete(null);
                handleGetAllPayments();
            } else {
                setError(result.message || 'Failed to delete payment details');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to delete payment details');
        }
    };

    // Update payment
    const handleUpdatePayment = async () => {
        if (!paymentToUpdate || !paymentToUpdate.id) return;
        setUpdateError(null);
        setSuccess(null);
        try {
            const result = await apiRequest<GenericResponseInterface<AdSpacePaymentDetailsInterface>>(
                `/paymentDetails/update/${paymentToUpdate.id}`,
                'PUT',
                paymentToUpdate
            );
            if (result.status) {
                setSuccess(result.message || 'Payment details updated successfully');
                setUpdateDialogOpen(false);
                setPaymentToUpdate(null);
                handleGetAllPayments();
            } else {
                setUpdateError(result.message || 'Failed to update payment details');
            }
        } catch (err: any) {
            setUpdateError(err.message || 'Failed to update payment details');
        }
    };

    const columns = getPaymentDetailsColumns(
        setPaymentToDelete,
        setDeleteDialogOpen,
        setPaymentToUpdate,
        setUpdateDialogOpen
    );

    useEffect(() => {
        handleGetAllPayments();
    }, []);

    return (
        <Box>
            <Typography variant="h6" gutterBottom>Payment Details Management</Typography>
            
            {/* Action Buttons */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <Button variant="contained" onClick={handleGetAllPayments}>
                    Get All Payments
                </Button>
            </Box>

            {/* Filters */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <TextField
                    label="Payment ID"
                    value={paymentId}
                    onChange={e => setPaymentId(e.target.value)}
                    size="small"
                />
                <Button variant="outlined" onClick={handleGetPaymentById}>
                    Search by ID
                </Button>

                <TextField
                    label="User ID"
                    value={userId}
                    onChange={e => setUserId(e.target.value)}
                    size="small"
                />
                <Button variant="outlined" onClick={handleGetPaymentsByUserId}>
                    Search by User
                </Button>

                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Status Filter</InputLabel>
                    <Select
                        value={statusFilter}
                        label="Status Filter"
                        onChange={e => {
                            setStatusFilter(e.target.value);
                            handleGetPaymentsByStatus(e.target.value);
                        }}
                    >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="paid">Paid</MenuItem>
                        <MenuItem value="failed">Failed</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            {/* Alerts */}
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            {/* Data Grid */}
            <Paper sx={{ width: '100%', overflow: 'auto', mt: 2 }}>
                {payments.length > 0 && (
                    <DataGrid
                        rows={payments.map(p => ({ ...p, id: p.id ?? Math.random() }))}
                        columns={columns}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 10, page: 0 } }
                        }}
                        pageSizeOptions={[10, 25, 50]}
                        autoHeight
                    />
                )}
                {payment && (
                    <DataGrid
                        rows={[{ ...payment, id: payment.id ?? Math.random() }]}
                        columns={columns}
                        hideFooter
                        autoHeight
                    />
                )}
            </Paper>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Alert severity="warning">
                        Are you sure you want to delete this payment record? This action cannot be undone.
                        Payment ID: {paymentToDelete?.id}
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button color="error" onClick={handleDeletePayment}>Delete</Button>
                </DialogActions>
            </Dialog>

            {/* Update Dialog */}
            <PaymentDetailsUpdateDialog
                open={updateDialogOpen}
                paymentToUpdate={paymentToUpdate}
                setPaymentToUpdate={setPaymentToUpdate}
                onClose={() => {
                    setUpdateDialogOpen(false);
                    setUpdateError(null);
                }}
                onSave={handleUpdatePayment}
                updateError={updateError}
            />
        </Box>
    );
};

export default PaymentDetailsAdminList;
